import { createFileRoute } from "@tanstack/react-router";
import SimpleTable from "@/components/ui/table";
import { useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { useEffect } from "react";
import FullPagination from "@/components/ui/shared/full-pagination";
import { SearchEngine } from "@/components/ui/shared/search-engine";
import { ChevronLeft, Loader2 } from "lucide-react";
import DataPlaceholder from "@/components/ui/shared/data-placeholder";
import { DetailsSalesTableColumns } from "@/components/sale/sale-meta";
import type { Sale } from "@/store/Sale";
import { useTranslate } from "@/hooks/use-translate";
import type { StockBatch } from "@/store/StockBatch";

export const Route = createFileRoute(
  "/(dasboard)/inventory/stock-batchs/$id/quantity-sold"
)({
  component: QuantitySoldPage,
});

function QuantitySoldPage() {
  const store = useAppStore();
  const stock_batch = store.stockBatch.current;
  const params = Route.useParams();
  const { t } = useTranslate();

  const searchForm = useSimpleForm({
    keyword: "",
  });

  const search = ({ page = 1, per_page = 10, stock_batch_id }: any = {}) => {
    store.sale.fetch({
      page,
      per_page,
      salable_id: stock_batch_id,
      status: "completed",
      ...searchForm.values,
    });
  };

  useEffect(() => {
    const id = params.id;
    if (id && store.auth.session) {
      store.stockBatch
        .fetchOne(id)
        .then((stock_batch: StockBatch) => {
          store.stockBatch.setCurrent(stock_batch);
          search({ salable_id: id });
        })
        .catch(store.errors.catch);
    }
  }, [params.id, store.auth.session]);

  if (!stock_batch) {
    return (
      <section>
        <>{store.errors.render()}</>
        <div className="h-24 bg-gray-200 rounded-md flex items-center justify-center">
          {store.loading.status.quantity ? (
            <div>
              <Loader2 className="animate-spin text-primary-400 mx-auto" />
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : (
            <div>
              <DataPlaceholder />
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section>
      <div>
        <button
          type="button"
          onClick={() => window.history.back()}
          className="py-2 inline-flex gap-2 items-center justify-center rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:underline font-semibold"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="">{t("Back")}</span>
        </button>
      </div>
      <h3 className="font-semibold mb-2"> {t("Quantity Sold")} </h3>
      <SearchEngine
        autoComplete={(values: any) => {
          store.sale.paginate(
            store.sale.items.filter((item: Sale) =>
              item.salable.batch_code?.toLowerCase().includes(values.keyword)
            )
          );
        }}
        selects={[]}
        form={searchForm}
        onSubmit={undefined}
      />
      <SimpleTable
        items={store.sale.pagination.data}
        columns={DetailsSalesTableColumns({ store })}
      />
      <FullPagination
        className="mt-4"
        canEdit={true}
        pagination={store.sale.pagination}
        onGoto={(page: number) => search({ page, salable_id: stock_batch.id })}
        onPerPage={(per_page: any) =>
          search({ per_page, salable_id: stock_batch.id })
        }
      />
    </section>
  );
}
